import clsx from "clsx";
import { useState, useRef } from "react";
import * as d3 from "d3";
import { ChartHeader } from "@/components/ChartHeader";
import { Select } from "@/components/Select";
import { Toggle } from "@/components/Toggle";
import { NoData } from "@/components/NoData";
import { getKeys } from "@/utils/keys";
import colors from "@/config/colors";
import sectorOptions from "@/config/trading-partners/sectors";
import {
  getEconomyDisplayName,
  formatMoney,
  formatMoneySr,
  formatPercentage,
  getSectorDisplayName,
  formatDisplayList,
  makePossessive,
  getSubSectorDisplayName,
  subSectorsSort,
} from "@/utils/format";
import { useFilter } from "@/hooks/useFilter";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { getSvgElement, useDimensions } from "../utils";
import * as Tooltip from "../Tooltip";
import * as Labels from "../Labels";
import { PartnerEvolutionDataRow } from "../App";
import styles from "./styles.module.scss";

const width = 1200;
const height = 500;
const xAxisHeight = 60;
const yAxisLabelTopOffset = 15;

// McKinsey categorical palette (first 5 colors)
const lineColors = [
  "electricBlue900",
  "cyan500",
  "marineGreen300",
  "electricBlue500",
  "marineGreen700",
] as const;

const measureOptions = [
  { value: "v", label: "Value, $" },
  { value: "s", label: "Shares, %" },
];

const partnerOptions = [
  { value: "c", label: "Individual" },
  { value: "r", label: "Regional" },
];

type LinesByCode = Record<
  PartnerEvolutionDataRow["Partner code"],
  PartnerEvolutionDataRow[]
>;

interface PartnerEvolutionProps {
  data: PartnerEvolutionDataRow[];
  economy: string;
  sector: (typeof sectorOptions)[number];
}

export function PartnerEvolution({
  data,
  economy,
  sector,
}: PartnerEvolutionProps) {
  const [tooltipYear, setTooltipYear] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<SVGSVGElement | null>(null);
  const [linesG, setLinesG] = useState<SVGGElement | null>(null);

  const is1600px = useMediaQuery("(min-width: 1600px)");
  const is1200px = useMediaQuery("(min-width: 1200px)");
  const is1024px = useMediaQuery("(min-width: 1024px)");
  const is500px = useMediaQuery("(min-width: 500px)");

  const rightPadding = is500px ? 175 : 225;
  const yAxisWidth = is1200px ? 75 : is1024px ? 95 : 115;
  const yAxisInset = is1200px ? 80 : is1024px ? 105 : 120;

  const svgDimensions = useDimensions(svg);
  const linesGDimensions = useDimensions(linesG);

  const directionOptionsSet = new Set<string>();
  data.forEach((row) => {
    directionOptionsSet.add(row.Direction);
  });
  const directionOptions = data.length
    ? Array.from(directionOptionsSet).sort((a, b) => a.localeCompare(b))
    : [];

  const { value: direction, onChange: onDirectionChange } = useFilter(
    "dir",
    directionOptions
  );

  const { value: measure, onChange: onMeasureChange } = useFilter(
    "toggle",
    direction?.toLowerCase() === "net export"
      ? ["v"]
      : (measureOptions.map((o) => o.value) as ["s", "v"])
  );

  const { value: partners, onChange: onPartnersChange } = useFilter(
    "toggle2",
    partnerOptions.map((o) => o.value) as ["s", "v"]
  );

  const subSectorOptionsSet = new Set<string>();
  data.forEach((row) => {
    subSectorOptionsSet.add(row["Sub-sector code"].toLowerCase());
  });
  const subSectorOptions = data.length
    ? Array.from(subSectorOptionsSet).sort(subSectorsSort)
    : [];

  const { value: subSector, onChange: onSubSectorChange } = useFilter(
    "sub-sector",
    subSectorOptions
  );

  const displayData = data.filter(
    (row) =>
      row.Direction === direction &&
      row["Partner toggle"] === partners.toUpperCase() &&
      row["Sub-sector code"].toLowerCase() === subSector
  );

  const allLines = displayData.reduce<LinesByCode>((acc, row) => {
    if (acc[row["Partner code"]]) {
      acc[row["Partner code"]].push(row);
    } else {
      acc[row["Partner code"]] = [row];
    }

    return acc;
  }, {} as LinesByCode);
  let topLines = getKeys(allLines)
    .sort((aKey, bKey) => {
      const aTotal = allLines[aKey].reduce((acc, row) => acc + row["Value"], 0);
      const bTotal = allLines[bKey].reduce((acc, row) => acc + row["Value"], 0);
      return bTotal - aTotal;
    })
    .map((key) =>
      allLines[key].sort((a, b) => parseInt(b["Year"]) - parseInt(a["Year"]))
    )
    .slice(0, 5);

  const xDomain = d3.extent(
    Object.values(topLines)
      .map((line) => line.map((row) => parseInt(row["Year"])))
      .flat()
  ) as number[];

  const firstYear = xDomain[0];
  const lastYear = xDomain[1];
  const years =
    firstYear && lastYear
      ? Array.from(Array(lastYear - firstYear + 1)).map((_, index) =>
          String(firstYear + index)
        )
      : [];

  // Show years with no data as 0
  topLines = topLines.map((rows) => {
    return years.map((year) => {
      const existingData = rows.find((r) => r.Year === year);
      return existingData || { ...rows[0], Year: year, Value: 0 };
    });
  });

  const xScale = d3
    .scaleLinear()
    .domain(xDomain)
    .range([0, width - rightPadding]);

  const getRowValue = (row?: PartnerEvolutionDataRow) => {
    if (!row) return 0;

    return measure === "s"
      ? (row["Value"] / row["Total reporter value"]) * 100
      : row["Value"];
  };

  const yDomain = d3.extent(
    Object.values(topLines)
      .map((line) => line.map((row) => getRowValue(row)))
      .flat()
  ) as number[];

  const yScale = d3
    .scaleLinear()
    .domain([yDomain[0] < 0 ? yDomain[0] : 0, yDomain[1] < 0 ? 0 : yDomain[1]])
    .range([height, 0]);

  const line = d3
    .line<PartnerEvolutionDataRow>()
    .x((d) => xScale(parseInt(d["Year"])))
    .y((d) => yScale(getRowValue(d)));

  const calculateTooltipYear = (
    event: React.MouseEvent<SVGSVGElement, MouseEvent>,
    container: HTMLDivElement | null
  ) => {
    const svgElement = getSvgElement(event.target as Element);

    if (!svgElement) {
      setTooltipYear(null);
      return;
    }

    const { width: svgWidth } = svgElement.getBoundingClientRect();
    const yAxisRelativeWidth = (yAxisWidth / width) * svgWidth;
    const yAxisPosition = is1600px ? 0 : yAxisRelativeWidth;

    const rightPaddingRelative =
      ((is1600px ? rightPadding : rightPadding - 50) / width) * svgWidth;

    const [x] = d3.pointer(event, container);

    if (x < yAxisPosition || x > svgWidth - rightPaddingRelative) {
      setTooltipYear(null);
      return;
    }

    const position =
      ((x - yAxisPosition) / (svgWidth - yAxisRelativeWidth)) * width;
    const year = Math.round(xScale.invert(position));

    // Make sure the year is within the domain, e.g. when hovering the axis d3
    // sometimes goes below the min value
    setTooltipYear(Math.max(xDomain[0], Math.min(xDomain[1], year)));
  };

  const yAxisInsetRelative = is1600px ? 0 : yAxisInset;

  // The x position of the year relative to the container
  const yearX =
    ((xScale(tooltipYear || lastYear) + yAxisInsetRelative) /
      (width + yAxisWidth)) *
    svgDimensions.width;

  // The xAxis height in pixel (instead of svg units)
  const relativeXAxisHeight = (xAxisHeight / height) * svgDimensions.height;

  const displaySector = getSectorDisplayName(sector);

  const labels = [...topLines]
    .sort((a, b) => {
      const aShare =
        getRowValue(
          a.find((row) => row["Year"] === (tooltipYear || lastYear).toString())
        ) || 0;
      const bShare =
        getRowValue(
          b.find((row) => row["Year"] === (tooltipYear || lastYear).toString())
        ) || 0;

      return bShare - aShare;
    })
    .map((line) => {
      const economyCode = line[0]["Partner code"];
      const colorIndex = topLines.findIndex(
        (l) => l[0]?.["Partner code"] === economyCode
      );

      const value = getRowValue(
        line.find((row) => row["Year"] === (tooltipYear || lastYear).toString())
      );
      const svgY = yScale(value || 0) + yAxisLabelTopOffset;

      const y = (svgY / (height + xAxisHeight)) * svgDimensions.height;

      return {
        economyCode,
        value,
        colorName: lineColors[colorIndex],
        y,
      };
    });

  let chartDescripion = `See how ${makePossessive(
    getEconomyDisplayName(economy)
  )} ${
    direction && direction.toLowerCase()
  }s of ${displaySector.toLowerCase()} with its top 5 trading partners have evolved`;

  if (topLines.length) {
    chartDescripion += ` since ${firstYear}.`;
  } else {
    chartDescripion += ` over time.`;
  }

  return (
    <div>
      <ChartHeader
        multipleControls={true}
        title={`${makePossessive(
          getEconomyDisplayName(economy)
        )} top trading partners over time`}
        description={chartDescripion}
      >
        <div
          className={clsx(
            styles["chart-header-controls"],
            styles["horizontal"]
          )}
        >
          <Select
            label="Direction of trade"
            items={directionOptions.map((o) => ({
              value: o,
            }))}
            value={direction}
            onChange={(value) => {
              onDirectionChange(value);
            }}
          />
          {subSectorOptions.length > 1 ? (
            <Select
              label="Sub-sector"
              items={subSectorOptions.map((o) => ({
                value: o,
                label: getSubSectorDisplayName(o),
              }))}
              value={subSector}
              onChange={(value) => {
                onSubSectorChange(value);
              }}
            />
          ) : null}
          <Toggle
            label="Measure of trade"
            options={measureOptions}
            value={measure}
            onValueChange={(value) => {
              if (value) {
                onMeasureChange(value);
              }
            }}
            disabled={direction?.toLowerCase() === "net export"}
          />
          <Toggle
            label="Partner economies"
            options={partnerOptions}
            value={partners}
            onValueChange={(value) => {
              if (value) {
                onPartnersChange(value);
              }
            }}
          />
        </div>
      </ChartHeader>

      {!topLines.length ? (
        <NoData />
      ) : (
        <>
          <div className={styles["chart-wrap"]} ref={containerRef}>
            <span className={styles.hide}>
              {`The top 5 trading partners of ${getEconomyDisplayName(
                economy
              )} are ${formatDisplayList(
                labels.map((label) => getEconomyDisplayName(label.economyCode))
              )}. In ${tooltipYear || lastYear}, ${formatDisplayList(
                labels.map(
                  (label) =>
                    `${getEconomyDisplayName(
                      label.economyCode
                    )} accounted for ${
                      measure === "s"
                        ? formatPercentage(label.value / 100)
                        : formatMoneySr(label.value)
                    }`
                )
              )}.`}
            </span>
            <svg
              viewBox={`0 0 ${width + yAxisWidth} ${height + xAxisHeight}`}
              onMouseEnter={(event) =>
                calculateTooltipYear(event, containerRef.current)
              }
              onMouseMove={(event) =>
                calculateTooltipYear(event, containerRef.current)
              }
              onMouseLeave={() => {
                setTooltipYear(null);
              }}
              ref={(ref) => setSvg(ref)}
              aria-hidden
            >
              {/* Make sure there is enough space for labels on top of the y axis */}
              <g
                transform={`translate(0,${yAxisLabelTopOffset})`}
                ref={(ref) => setLinesG(ref)}
              >
                {/* Y axis + ticks */}
                <g
                  transform={`translate(${
                    yAxisWidth - yAxisLabelTopOffset
                  }, 0)`}
                  className={styles["axis"]}
                >
                  {yScale.ticks(5).map((tick) => {
                    const y = yScale(tick);
                    return (
                      <g
                        key={tick}
                        transform={`translate(0, ${y})`}
                        className={styles["tick"]}
                      >
                        <text x={-yAxisWidth + 15} dominantBaseline="middle">
                          {measure === "s"
                            ? `${tick}%`
                            : formatMoney(tick, true)}
                        </text>
                      </g>
                    );
                  })}
                </g>

                <g transform={`translate(${yAxisInset}, 0)`}>
                  {yScale.ticks(5).map((tick) => {
                    const y = yScale(tick);
                    return (
                      <line
                        key={tick}
                        className={styles["axis-tickline"]}
                        x1={0}
                        x2={width - rightPadding}
                        y1={y}
                        y2={y}
                      />
                    );
                  })}
                </g>

                {/* X axis + ticks */}
                <g
                  transform={`translate(${yAxisInset}, ${height})`}
                  className={styles["axis"]}
                >
                  <line
                    x={0}
                    x1={width - rightPadding}
                    className={styles["axis-line"]}
                  />
                  {xScale.ticks(5).map((tick) => {
                    const x = xScale(tick);
                    return (
                      <g
                        key={tick}
                        transform={`translate(${x} 0)`}
                        textAnchor={
                          x < 20 ? "start" : x > width - 100 ? "end" : "middle"
                        }
                        className={styles["tick"]}
                      >
                        <text y="40">{tick}</text>
                      </g>
                    );
                  })}
                </g>

                {/* Data lines */}
                <g
                  transform={`translate(${yAxisInset}, 0)`}
                  className={styles.lines}
                >
                  {topLines.map((l, i) => (
                    <path
                      className={styles["top-line"]}
                      key={l[0]["Partner code"]}
                      d={line(l) || ""}
                      fill="none"
                      stroke={colors[lineColors[i]]}
                    />
                  ))}
                </g>
              </g>
            </svg>
            {tooltipYear ? (
              <Tooltip.Root
                year={tooltipYear}
                x={yearX}
                bottom={relativeXAxisHeight}
                containerX={svgDimensions.x}
              >
                {labels.map((label) => (
                  <Tooltip.Item
                    key={label.economyCode}
                    economyCode={label.economyCode}
                    value={label.value}
                    isPercent={measure === "s"}
                    colorName={label.colorName}
                    y={label.y}
                  />
                ))}
              </Tooltip.Root>
            ) : (
              <Labels.Root
                x={yearX}
                bottom={relativeXAxisHeight}
                left={linesGDimensions.right}
              >
                {labels.map((label) => (
                  <Labels.Item
                    // We need to combine economy code and y to make sure the
                    // component gets re-created if the filters change and this
                    // economy code is still in the new data
                    key={`${label.economyCode}-${label.y}`}
                    economyCode={label.economyCode}
                    colorName={label.colorName}
                    y={label.y}
                  />
                ))}
              </Labels.Root>
            )}
          </div>
        </>
      )}
    </div>
  );
}
